
import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchSiteOffices, SiteOffice } from '@/services/siteOfficeService';

interface SiteOfficeContextType {
  selectedSiteOffice: string | null;
  selectedSiteOfficeData: SiteOffice | null;
  siteOffices: SiteOffice[];
  accessibleSiteOffices: SiteOffice[]; // Site offices that the current user can access
  loading: boolean;
  userSiteOfficeId: string | null; // Current user's assigned site office (null = all)
  canAccessAllSiteOffices: boolean;
  setSiteOffice: (office: string) => void;
  clearSiteOffice: () => void;
  refreshSiteOffices: () => Promise<void>;
  setUserSiteOfficeAccess: (siteOfficeId: string | null) => void;
  canAccessSiteOffice: (siteOfficeId: string) => boolean;
}

const SiteOfficeContext = createContext<SiteOfficeContextType | undefined>(undefined);

export const useSiteOffice = () => {
  const context = useContext(SiteOfficeContext);
  if (context === undefined) {
    throw new Error('useSiteOffice must be used within a SiteOfficeProvider');
  }
  return context;
};

interface SiteOfficeProviderProps {
  children: React.ReactNode;
}

export const SiteOfficeProvider = ({ children }: SiteOfficeProviderProps) => {
  const [selectedSiteOffice, setSelectedSiteOffice] = useState<string | null>(null);
  const [selectedSiteOfficeData, setSelectedSiteOfficeData] = useState<SiteOffice | null>(null);
  const [siteOffices, setSiteOffices] = useState<SiteOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [userSiteOfficeId, setUserSiteOfficeId] = useState<string | null>(null);

  const loadSiteOffices = async () => {
    try {
      setLoading(true);
      const offices = await fetchSiteOffices();
      if (offices && offices.length > 0) {
        setSiteOffices(offices);
        // Cache in localStorage for resilience against DB timeouts
        localStorage.setItem('cachedSiteOffices', JSON.stringify(offices));
      } else {
        // Fallback to cached data if DB returns empty (e.g. timeout)
        const cached = localStorage.getItem('cachedSiteOffices');
        if (cached) {
          try {
            const cachedOffices = JSON.parse(cached);
            if (cachedOffices.length > 0) {
              setSiteOffices(cachedOffices);
            }
          } catch {}
        }
      }
    } catch (error) {
      console.error('Failed to load site offices:', error);
      // Fallback to cached data on error
      const cached = localStorage.getItem('cachedSiteOffices');
      if (cached) {
        try {
          setSiteOffices(JSON.parse(cached));
        } catch {}
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load saved site office from localStorage on init
    const saved = localStorage.getItem('selectedSiteOffice');
    if (saved) {
      setSelectedSiteOffice(saved);
    }
    
    // Load user's site office access from localStorage
    const savedUserSiteOffice = localStorage.getItem('userSiteOfficeId');
    if (savedUserSiteOffice) {
      setUserSiteOfficeId(savedUserSiteOffice === 'null' ? null : savedUserSiteOffice);
    }
    
    // Load site offices from database
    loadSiteOffices();
  }, []);

  // Update selectedSiteOfficeData when selectedSiteOffice changes
  useEffect(() => {
    if (selectedSiteOffice && siteOffices.length > 0) {
      const officeData = siteOffices.find(office => office.name === selectedSiteOffice);
      setSelectedSiteOfficeData(officeData || null);
    } else {
      setSelectedSiteOfficeData(null);
    }
  }, [selectedSiteOffice, siteOffices]);

  // Check if user can access all site offices (null = all access)
  const canAccessAllSiteOffices = userSiteOfficeId === null;

  // Get accessible site offices based on user's assignment
  const accessibleSiteOffices = canAccessAllSiteOffices 
    ? siteOffices 
    : siteOffices.filter(office => office.id === userSiteOfficeId);

  // Check if user can access a specific site office
  const canAccessSiteOffice = (siteOfficeId: string): boolean => {
    if (canAccessAllSiteOffices) return true;
    return userSiteOfficeId === siteOfficeId;
  };

  const setSiteOffice = (office: string) => {
    // Validate that user can access this site office
    const officeData = siteOffices.find(o => o.name === office);
    if (officeData && !canAccessSiteOffice(officeData.id)) {
      console.warn('User does not have access to this site office:', office);
      return;
    }
    
    // Always clear old cached IDs first to prevent stale UUID issues
    localStorage.removeItem('selectedSiteOfficeId');
    localStorage.removeItem('selectedSiteOfficeUuid');
    
    setSelectedSiteOffice(office);
    localStorage.setItem('selectedSiteOffice', office);
    
    // Set new UUID if office data is available
    if (officeData) {
      localStorage.setItem('selectedSiteOfficeId', officeData.id);
      localStorage.setItem('selectedSiteOfficeUuid', officeData.id);
    }
  };

  const clearSiteOffice = () => {
    setSelectedSiteOffice(null);
    setSelectedSiteOfficeData(null);
    localStorage.removeItem('selectedSiteOffice');
  };

  const refreshSiteOffices = async () => {
    await loadSiteOffices();
  };

  // Set user's site office access (called after login)
  const setUserSiteOfficeAccess = (siteOfficeId: string | null) => {
    setUserSiteOfficeId(siteOfficeId);
    localStorage.setItem('userSiteOfficeId', siteOfficeId === null ? 'null' : siteOfficeId);
    
    // If user has specific site office, auto-select it
    if (siteOfficeId !== null) {
      const office = siteOffices.find(o => o.id === siteOfficeId);
      if (office) {
        setSelectedSiteOffice(office.name);
        localStorage.setItem('selectedSiteOffice', office.name);
      }
    }
  };

  const value = {
    selectedSiteOffice,
    selectedSiteOfficeData,
    siteOffices,
    accessibleSiteOffices,
    loading,
    userSiteOfficeId,
    canAccessAllSiteOffices,
    setSiteOffice,
    clearSiteOffice,
    refreshSiteOffices,
    setUserSiteOfficeAccess,
    canAccessSiteOffice,
  };

  return (
    <SiteOfficeContext.Provider value={value}>
      {children}
    </SiteOfficeContext.Provider>
  );
};
